/**
 * The spreadsheet people are asked to fill in.
 *
 * Here rather than in the API because the browser builds and downloads it with
 * no request at all, and because the column list is the same vocabulary the
 * importer reads — one definition, two users, so a column cannot be offered in
 * the template and then not understood on the way back in.
 */

/** Column order is also the export order, and what the import expects by name. */
export const CSV_COLUMNS = [
  'household',
  'firstName',
  'lastName',
  'ageBand',
  'email',
  'phone',
  'side',
  'tags',
  'rsvp',
  'likelihood',
  'mealChoice',
  'isPlaceholder',
  'dietaryNotes',
  'accessibilityNotes',
  'notes',
  'line1',
  'line2',
  'city',
  'state',
  'postalCode',
  'country',
  'invitationSentAt',
  'thankYouSentAt',
  'giftReceivedAt',
  'giftNote',
] as const;

export type CsvColumn = (typeof CSV_COLUMNS)[number];

/**
 * A template in the language the app is being read in.
 *
 * The importer accepts Spanish column names, so a Spanish template is not a
 * translation layer — it is the same file with headers somebody can read. The
 * alternative is handing a Spanish-speaking couple a file headed `ageBand` and
 * `thankYouSentAt` and expecting them to guess.
 */
const HEADERS_ES: Record<CsvColumn, string> = {
  household: 'Familia',
  firstName: 'Nombre',
  lastName: 'Apellido',
  ageBand: 'Edad',
  email: 'Correo',
  phone: 'Teléfono',
  side: 'De parte de',
  tags: 'Etiquetas',
  rsvp: 'Confirmación',
  likelihood: 'Probabilidad',
  mealChoice: 'Menú',
  isPlaceholder: 'Sin nombre aún',
  dietaryNotes: 'Alergias',
  accessibilityNotes: 'Accesibilidad',
  notes: 'Notas',
  line1: 'Dirección',
  line2: 'Dirección 2',
  city: 'Ciudad',
  state: 'Departamento',
  postalCode: 'Código postal',
  country: 'País',
  invitationSentAt: 'Invitación enviada',
  thankYouSentAt: 'Tarjeta enviada',
  giftReceivedAt: 'Fecha regalo',
  giftNote: 'Regalo',
};

const HEADERS_EN: Record<CsvColumn, string> = {
  household: 'Household',
  firstName: 'First name',
  lastName: 'Last name',
  ageBand: 'Age',
  email: 'Email',
  phone: 'Phone',
  side: 'Side',
  tags: 'Tags',
  rsvp: 'RSVP',
  likelihood: 'Likelihood',
  mealChoice: 'Meal',
  isPlaceholder: 'Unnamed guest',
  dietaryNotes: 'Allergies',
  accessibilityNotes: 'Accessibility',
  notes: 'Notes',
  line1: 'Address',
  line2: 'Address 2',
  city: 'City',
  state: 'State',
  postalCode: 'Postal code',
  country: 'Country',
  invitationSentAt: 'Invitation sent',
  thankYouSentAt: 'Thank-you sent',
  giftReceivedAt: 'Gift received',
  giftNote: 'Gift',
};

/**
 * Two example rows, which are the actual documentation.
 *
 * A header alone does not say whether `Edad` wants a number or a word, what a
 * date looks like, or that two people sharing a `Familia` become one invitation.
 * These answer all three by example, which is the only form of instruction
 * anybody reads inside a spreadsheet.
 *
 * Obviously fake on purpose. They are also visible in the review screen before
 * anything is saved, so one left in is removed with a click rather than becoming
 * a guest.
 */
const EXAMPLES: Record<'en' | 'es', Partial<Record<CsvColumn, string>>[]> = {
  en: [
    {
      household: 'The Rivera family',
      firstName: 'Alex',
      lastName: 'Rivera',
      ageBand: 'adult',
      email: 'alex@example.com',
      side: 'his',
      rsvp: 'pending',
      likelihood: 'likely',
      city: 'Cartagena',
      country: 'Colombia',
    },
    { household: 'The Rivera family', firstName: 'Mia', lastName: 'Rivera', ageBand: 'child' },
  ],
  es: [
    {
      household: 'Familia Rivera',
      firstName: 'Alex',
      lastName: 'Rivera',
      ageBand: 'adulto',
      email: 'alex@ejemplo.com',
      side: 'él',
      rsvp: 'pendiente',
      likelihood: 'probable',
      city: 'Cartagena',
      country: 'Colombia',
    },
    { household: 'Familia Rivera', firstName: 'Mía', lastName: 'Rivera', ageBand: 'niño' },
  ],
};

const quote = (value: string): string =>
  /[",;\n\r]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;

/**
 * The template, as a CSV.
 *
 * **Separated by semicolons, with a byte-order mark.** Both are for Excel, which
 * is what these files get opened in. Excel splits on the list separator of the
 * machine's locale — a semicolon across Spain and Latin America — and without
 * the BOM it reads a UTF-8 file as Latin-1, so `Teléfono` arrives as `TelÃ©fono`
 * in the column heading of a file we wrote ourselves. The importer detects the
 * separator either way, so this costs nothing on the way back in.
 */
export function guestTemplateCsv(locale: 'en' | 'es'): string {
  const headers = locale === 'es' ? HEADERS_ES : HEADERS_EN;
  const rows = [
    CSV_COLUMNS.map((c) => quote(headers[c])).join(';'),
    ...EXAMPLES[locale].map((row) => CSV_COLUMNS.map((c) => quote(row[c] ?? '')).join(';')),
  ];
  return `\uFEFF${rows.join('\r\n')}\r\n`;
}
